import EditIcon from "@mui/icons-material/Edit";
import { Box, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro-6";
import React from "react";
import { useTranslation } from "react-i18next";

import IconGroup from "../../../design/icon-group";
import Highlight from "../../Highlight";
import { TimeOffBalance } from "../store/time-off-balances-report-types";

const useTimeOffBalancesReportColumn = (
  searchText: string | undefined,
  isBadgeNumberEnabled: boolean,
  canUpdateLeaveBalance: boolean,
  onEdit: (row: TimeOffBalance) => void
) => {
    const { t } = useTranslation();
    const columns: GridColDef[] = [
        {
			field: "workerId",
			headerName: t("time-off-balances_worker-id"),
			flex: 1,
			minWidth: 100,
			renderHeader: () => {
                return <span style={{ fontWeight: "bold" }}>{t("time-off-balances_worker-id")}</span>
            },
			renderCell: (params) => {
                const companyEmployeeId = params.row.companyEmployeeId;
				return (
                    <Tooltip title={companyEmployeeId}>
                        <span style={{ textOverflow: "ellipsis", overflow: "hidden" }}>
                            <Highlight searchText={searchText} value={companyEmployeeId || "-"} />
                        </span>
                    </Tooltip>
                );
            },
            sortComparator: (v1, v2) => {
                const getNumericPart = (value: string | undefined | null): number | null => {
                    if (!value) return null;
                    const trimmedValue = value.trim();
                    const match = trimmedValue.match(/^\d+$/);
                    return match ? parseInt(trimmedValue, 10) : null;
                };

                const isBlank = (value: string | undefined | null) =>
                    value === null || value === undefined || value.trim() === "";

                const blank1 = isBlank(v1);
                const blank2 = isBlank(v2);

                if (blank1 && blank2) return 0;
                if (blank1) return 1;
                if (blank2) return -1;

                const num1 = getNumericPart(v1);
                const num2 = getNumericPart(v2);

                if (num1 !== null && num2 !== null) {
                    return num1 - num2;
                }

                if (num1 !== null) return -1;
                if (num2 !== null) return 1;

                return v1.localeCompare(v2);
            },
		},
        {
            field: "fullName",
            headerName: t("time-off-balances_full-name"),
            flex: 1,
            minWidth: 130,
            renderHeader: () => {
                return <span style={{ fontWeight: "bold" }}>{t("time-off-balances_full-name")}</span>
            },
            renderCell: (params) => {
                const fullName = params.row.fullName;
                return (
                    <Tooltip title={fullName}>
                        <span style={{ textOverflow: "ellipsis", overflow: "hidden" }}>
                            <Highlight searchText={searchText} value={fullName || "-"} />
                        </span>
                    </Tooltip>
                );
            }
        },
        {
            field: "timeOffType",
            headerName: t("time-off-balances_time-off-type"),
            flex: 1,
            minWidth: 130,
            renderHeader: () => {
                return <span style={{ fontWeight: "bold" }}>{t("time-off-balances_time-off-type")}</span>
            },
            renderCell: (params) => {
                const timeOffType = params.row.timeOffType;
                return (
                    <Tooltip title={timeOffType}>
                        <span style={{ textOverflow: "ellipsis", overflow: "hidden" }}>
                            <Highlight searchText={searchText} value={timeOffType || "-"} />
                        </span>
                    </Tooltip>
                );
            }
        },
        {
            field: "balanceMinutes",
            headerName: t("time-off-balances_balance"),
            flex: 1,
            minWidth: 110,
            renderHeader: () => {
                return <span style={{ fontWeight: "bold" }}>{t("time-off-balances_balance")}</span>
            },
            renderCell: (params) => {
                const formattedBalance = params.row.balanceFormatted; // Use pre-formatted value for display
                return (
                    <Tooltip title={formattedBalance}>
                        <span style={{ textOverflow: "ellipsis", overflow: "hidden" }}>
                            <Highlight searchText={searchText} value={formattedBalance} />
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            field: "jobTitle",
            headerName: t("time-off-balances_job-title"),
            flex: 1,
            minWidth: 130,
            renderHeader: () => {
                return <span style={{ fontWeight: "bold" }}>{t("time-off-balances_job-title")}</span>
            },
            renderCell: (params) => {
                const jobTitle = params.row.jobTitle;
                return (
                    <Tooltip title={jobTitle}>
                        <span style={{ textOverflow: "ellipsis", overflow: "hidden" }}>
                            <Highlight searchText={searchText} value={jobTitle || "-"} />
                        </span>
                    </Tooltip>
                );
            }
        },
        {
            field: "workcenter",
            headerName: t("time-off-balances_work-center"),
            flex: 1,
            minWidth: 130,
            renderHeader: () => {
                return <span style={{ fontWeight: "bold" }}>{t("time-off-balances_work-center")}</span>
            },
            renderCell: (params) => {
                const workcenter = params.row.workcenter;
                return (
                    <Tooltip title={workcenter}>
                        <span style={{ textOverflow: "ellipsis", overflow: "hidden" }}>
                            <Highlight searchText={searchText} value={workcenter || "-"} />
                        </span>
                    </Tooltip>
                );
            }
        },
        {
            field: "location",
            headerName: t("time-off-balances_location"),
            flex: 1,
            minWidth: 130,
            renderHeader: () => {
                return <span style={{ fontWeight: "bold" }}>{t("time-off-balances_location")}</span>
            },
            renderCell: (params) => {
                const location = params.row.location;
                return (
                    <Tooltip title={location}>
                        <span style={{ textOverflow: "ellipsis", overflow: "hidden" }}>
                            <Highlight searchText={searchText} value={location || "-"} />
                        </span>
                    </Tooltip>
                );
            }
        },
        {
            field: "shiftPattern",
            headerName: t("time-off-balances_shift-pattern"),
            flex: 1,
            minWidth: 120,
            renderHeader: () => {
                return <span style={{ fontWeight: "bold" }}>{t("time-off-balances_shift-pattern")}</span>
            },
            renderCell: (params) => {
                const shiftPattern = params.row.shiftPattern;
                return (
                    <Tooltip title={shiftPattern}>
                        <span style={{ textOverflow: "ellipsis", overflow: "hidden" }}>
                            <Highlight searchText={searchText} value={shiftPattern || "-"} />
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            field: "reportingManager",
            headerName: t("time-off-balances_reporting-manager"),
            flex: 1,
            minWidth: 150,
            renderHeader: () => {
                return <span style={{ fontWeight: "bold" }}>{t("time-off-balances_reporting-manager")}</span>
            },
            renderCell: (params) => {
                const reportingManager = params.row.reportingManager;
                return (
                    <Tooltip title={reportingManager}>
                        <span style={{ textOverflow: "ellipsis", overflow: "hidden" }}>
                            <Highlight searchText={searchText} value={reportingManager || "-"} />
                        </span>
                    </Tooltip>
                );
            }
        },
    ];
     if (isBadgeNumberEnabled) {
        columns.splice(1, 0,
         {
			field: "badgeNumber",
			headerName: t("time-off-balances_badge-number"),
			flex: 1,
			minWidth: 100,
			renderHeader: () => {
                return <span style={{ fontWeight: "bold" }}>{t("time-off-balances_badge-number")}</span>
            },
			renderCell: (params) => {
                const badgeNumber = params.row.badgeNumber;
				return (
                    <Tooltip title={badgeNumber}>
                        <span style={{ textOverflow: "ellipsis", overflow: "hidden" }}>
                            <Highlight searchText={searchText} value={badgeNumber || "-"} />
                        </span>
                    </Tooltip>
                );
            },
            sortComparator: (v1, v2) => {
                const getNumericPart = (value: string | undefined | null): number | null => {
                    if (!value) return null;
                    const trimmedValue = value.trim();
                    const match = trimmedValue.match(/^\d+$/);
                    return match ? parseInt(trimmedValue, 10) : null;
                };

                const isBlank = (value: string | undefined | null) =>
                    value === null || value === undefined || value.trim() === "";

                const blank1 = isBlank(v1);
                const blank2 = isBlank(v2);

                if (blank1 && blank2) return 0;
                if (blank1) return 1;
                if (blank2) return -1;

                const num1 = getNumericPart(v1);
                const num2 = getNumericPart(v2);

                if (num1 !== null && num2 !== null) {
                    return num1 - num2;
                }

                if (num1 !== null) return -1;
                if (num2 !== null) return 1;

                return v1.localeCompare(v2);
            },
		})
    }
   if (canUpdateLeaveBalance) {
     columns.push({
       field: "actions",
       headerName: t("common_actions"),
       headerAlign: "center",
       align: "center",
       cellClassName: "actions-col-cell",
       headerClassName: "actions-col-header",
       width: 86,
       maxWidth: 86,
       sortable: false,
       filterable: false,
       disableColumnMenu: true,
       renderHeader: () => (
         <span
           style={{
             fontWeight: "bold",
             width: "100%",
             display: "flex",
             justifyContent: "center",
             alignItems: "center",
           }}
         >
					{t("common_actions")}
				</span>
       ),
       renderCell: (params) => {
         const isNoLimit = params.row.isNoLimit;
         return (
             <Box
               sx={{
                 display: "flex",
                 alignItems: "center",
                 justifyContent: "center",
                 width: "100%",
               }}
             >
               <IconGroup icons={[
                 {
                   icon: <EditIcon
                     id={`edit-job-${params?.row?.id}-enabled`}
                     sx={{
                       fontSize: "16px",
                     }}
                   />,
                   tooltip: isNoLimit ? t("time-off-balances_no-limit") : t("time-off-balances_update-balance"),
                   hoverColor: isNoLimit ? "rgba(0, 0, 0, 0.26)" : undefined,
                   onClick: (e: React.MouseEvent) => {
                     e.stopPropagation();
                     if (!isNoLimit) {
                       onEdit(params.row);
                     }
                   }
                 }
               ]}
               defaultColor={isNoLimit ? "rgba(0, 0, 0, 0.26)" : undefined}
               />
             </Box>
         );
       },
     })
   }
	return columns;
};

export default useTimeOffBalancesReportColumn;
